import RestClientService from "@/core/services/restClient.service";

import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import { formatString, formatStringToRoutePath } from "@/core/utils/helpers";

import {
  NEW_FLIGHT_BOOKING_API_PATH,
  BOOKING_API_PATH,
  DOWNLOAD_FLIGHT_TICKET,
  CANCEL_BOOKINGS_API_PATH,
  VOID_TICKET,
  ORGANIZATION_PASSENGERSS,
  SEND_AIRLINE_TICKET,
  ADD_ONS_PATH,
  FLIGHT_ISSUANCE_API_PATH,
  FLIGHTS_PROMO_API_PATH,
} from "@/core/constants/apiEndPoints";
import {
  GetBookingPayloadType,
  GetDownloadBookingPayloadType,
  ICancelBookingPayloadType,
  IConfirmBooking,
  ICommitVoidBookingPayloadType,
  IInitiateVoidBookingPayloadType,
  Traveler,
} from "@/core/types/flight/FlightBookingForm";

import { ConfirmAddOns } from "@/modules/Booking/constants";
import { EditPassengerRequest } from "@/core/types/flight";
import { APIResponse } from "@/modules/FlightSearchResult/types";
import { FlightBookingResponse } from "../types";
import type {
  ApplyTicketIssuancePromoResponse,
  FetchTicketIssuanceDetailsPayload,
  IssueTicketPayload,
  TicketIssuanceDetailsResponse,
} from "../types/component.types";

export interface SendAirlineTicketBodyRequest {
  pnr: string;
  airline_provider: string;
  emails: string[];
}

class FlightBookingService {
  private restClient: RestClientService;

  constructor() {
    this.restClient = new RestClientService();
  }

  private formatCurrency(amount: number, currency = "PKR"): string {
    return `${currency} ${amount.toLocaleString()}`;
  }

  private mapPriceAmount(
    field: unknown,
  ): ApplyTicketIssuancePromoResponse["originalPrice"] {
    const price = field as { value?: number; currency?: string } | undefined;

    if (typeof price?.value !== "number") {
      return null;
    }

    return {
      value: price.value,
      currency: price.currency || "PKR",
    };
  }

  private getFirstPriceField(
    details: Record<string, unknown>,
    keys: string[],
  ): { value?: number; currency?: string } | undefined {
    for (const key of keys) {
      const field = details[key] as
        { value?: number; currency?: string } | undefined;

      if (typeof field?.value === "number") {
        return field;
      }
    }

    return undefined;
  }

  private sumPriceFieldAcrossDetails(
    priceDetails: Record<string, unknown>[],
    keys: string[],
  ): { value: number; currency: string } | undefined {
    let total = 0;
    let currency: string | undefined;
    let hasValue = false;

    for (const detail of priceDetails) {
      const field = this.getFirstPriceField(detail, keys);

      if (typeof field?.value === "number" && field.value !== 0) {
        total += field.value;
        currency = field.currency || currency;
        hasValue = true;
      }
    }

    if (!hasValue) {
      return undefined;
    }

    return { value: total, currency: currency || "PKR" };
  }

  private aggregatePriceDetails(
    priceDetails: Record<string, unknown>[],
  ): Record<string, unknown> {
    const fieldMappings = [
      { key: "base_fare", sources: ["base_fare"] },
      { key: "total_tax", sources: ["total_tax", "tax"] },
      { key: "gross_fare", sources: ["gross_fare"] },
      { key: "fee", sources: ["fee", "fees"] },
      { key: "additional_charges", sources: ["additional_charges"] },
      { key: "service_tax", sources: ["service_tax"] },
      { key: "surcharges", sources: ["surcharges"] },
      { key: "surcharges_xi", sources: ["surcharges_xi"] },
      { key: "surcharges_xii", sources: ["surcharges_xii"] },
      { key: "esc_charges", sources: ["esc_charges"] },
      { key: "total_amount", sources: ["total_amount"] },
      {
        key: "total_amount_after_pricing",
        sources: ["total_amount_after_pricing"],
      },
      { key: "deal_promo_discount", sources: ["deal_promo_discount"] },
      {
        key: "fare_difference",
        sources: ["fare_difference", "price_difference"],
      },
    ] as const;

    const aggregated: Record<string, unknown> = {};

    fieldMappings.forEach(({ key, sources }) => {
      const summed = this.sumPriceFieldAcrossDetails(priceDetails, [
        ...sources,
      ]);
      if (summed) {
        aggregated[key] = summed;
      }
    });

    const discountData = priceDetails.find(
      (detail) =>
        (detail.discount_data as { discount_code?: string } | undefined)
          ?.discount_code,
    )?.discount_data;

    if (discountData) {
      aggregated.discount_data = discountData;
    }

    return aggregated;
  }

  private getFareDifferenceRow(
    details: Record<string, unknown>,
  ): { label: string; amount: string } | null {
    const field = this.getFirstPriceField(details, [
      "fare_difference",
      "price_difference",
    ]);

    if (typeof field?.value !== "number" || field.value === 0) {
      return null;
    }

    const label = field.value > 0 ? "Fees" : "Discount";
    const formattedAmount = this.formatCurrency(
      Math.abs(field.value),
      field.currency,
    );

    return {
      label,
      amount: field.value > 0 ? formattedAmount : `-${formattedAmount}`,
    };
  }

  private toPricingRows(details: Record<string, unknown>) {
    const rowMappings = [
      { keys: ["base_fare"], label: "Base Fare" },
      { keys: ["surcharges"], label: "Surcharges" },
      { keys: ["surcharges_xi"], label: "Surcharges XI" },
      { keys: ["surcharges_xii"], label: "Surcharges XII" },
      { keys: ["total_tax", "tax"], label: "Tax" },
      { keys: ["fee", "fees"], label: "Fees" },
      { keys: ["gross_fare"], label: "Gross Fare" },
      { keys: ["additional_charges"], label: "Additional Charges" },
      { keys: ["service_tax"], label: "Service Tax" },
      { keys: ["esc_charges"], label: "ESC Charges" },
      {
        keys: ["deal_promo_discount"],
        label: "Deal Discount",
        isDiscount: true,
      },
    ] as const;

    const rows = rowMappings.reduce<Array<{ label: string; amount: string }>>(
      (accumulatedRows, mapping) => {
        const value = this.getFirstPriceField(details, [...mapping.keys]);

        if (typeof value?.value !== "number" || value.value === 0) {
          return accumulatedRows;
        }

        const formattedAmount = this.formatCurrency(
          value.value,
          value.currency,
        );

        accumulatedRows.push({
          label: mapping.label,
          amount:
            "isDiscount" in mapping && mapping.isDiscount
              ? `-${formattedAmount}`
              : formattedAmount,
        });

        return accumulatedRows;
      },
      [],
    );

    const fareDifferenceRow = this.getFareDifferenceRow(details);

    if (fareDifferenceRow) {
      const dealDiscountIndex = rows.findIndex(
        (row) => row.label === "Deal Discount",
      );

      if (dealDiscountIndex >= 0) {
        rows.splice(dealDiscountIndex, 0, fareDifferenceRow);
      } else {
        rows.push(fareDifferenceRow);
      }
    }

    return rows;
  }

  private resolveFareUpdated(
    responseData: Record<string, unknown>,
    selectedFarePublicIds: string[] = [],
  ): boolean {
    if (responseData.fare_updated === true) {
      return true;
    }

    const calculatedFare = responseData.calculated_fare as
      Record<string, unknown> | undefined;
    if (calculatedFare?.fare_updated === true) {
      return true;
    }

    const priceDetails =
      (responseData.booking_price_details as
        Array<Record<string, unknown>> | undefined) ?? [];

    if (selectedFarePublicIds.length > 0) {
      return priceDetails
        .filter((item) =>
          selectedFarePublicIds.includes(String(item.public_id ?? "")),
        )
        .some((item) => item.fare_updated === true);
    }

    return priceDetails.some((item) => item.fare_updated === true);
  }

  private resolvePricing(
    responseData: Record<string, unknown>,
  ): TicketIssuanceDetailsResponse["pricing"] {
    const calculatedFare = responseData.calculated_fare as
      Record<string, unknown> | undefined;
    const priceDetails =
      (calculatedFare?.booking_price_details as
        Record<string, unknown>[] | undefined) ??
      (responseData.booking_price_details as
        Record<string, unknown>[] | undefined) ??
      [];

    const source =
      priceDetails.length > 0
        ? this.aggregatePriceDetails(priceDetails)
        : (calculatedFare ?? responseData);

    const rows = this.toPricingRows(source);

    const totalAmountField =
      (source.gross_fare as
        { value?: number; currency?: string } | undefined) ??
      (source.total_amount as
        { value?: number; currency?: string } | undefined);
    const grandTotalField =
      (calculatedFare?.total_amount_after_pricing as
        { value?: number; currency?: string } | undefined) ??
      (responseData.total_amount_after_pricing as
        { value?: number; currency?: string } | undefined) ??
      (source.total_amount_after_pricing as
        { value?: number; currency?: string } | undefined) ??
      totalAmountField;
    const discountField =
      (responseData.total_deal_promo_discount as
        { value?: number; currency?: string } | undefined) ??
      (calculatedFare?.total_deal_promo_discount as
        { value?: number; currency?: string } | undefined) ??
      (source.deal_promo_discount as
        { value?: number; currency?: string } | undefined);
    const discountCode =
      (source.discount_data as { discount_code?: string } | undefined)
        ?.discount_code || "PROMO";

    const totalAmount =
      typeof totalAmountField?.value === "number"
        ? this.formatCurrency(totalAmountField.value, totalAmountField.currency)
        : "N/A";

    const grandTotal =
      typeof grandTotalField?.value === "number"
        ? this.formatCurrency(grandTotalField.value, grandTotalField.currency)
        : totalAmount;

    return {
      rows,
      totalAmount,
      dealDiscount:
        typeof discountField?.value === "number" && discountField.value !== 0
          ? {
              code: discountCode,
              amount: this.formatCurrency(
                discountField.value,
                discountField.currency,
              ),
            }
          : undefined,
      grandTotal,
    };
  }

  public getNewBooking(
    booking_id: string,
  ): Promise<APIResponse<FlightBookingResponse>> {
    const PATH =
      formatStringToRoutePath(NEW_FLIGHT_BOOKING_API_PATH.GET, {
        id: booking_id,
      }) + "/";

    const response = this.restClient.get(PATH) as Promise<
      APIResponse<FlightBookingResponse>
    >;

    return response;
  }

  public async sendAirlineTicket(payload: SendAirlineTicketBodyRequest) {
    try {
      const response: ICoreResponse = await this.restClient.post(
        SEND_AIRLINE_TICKET.POST,
        payload,
      );

      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.AIRLINE_TICKETS_SENT_SUCCESS;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch (err) {
      console.error("Error in sendAirlineTickets:", err);
    }
    notificationService.triggerNotification();
  }
  public getAllAddOns(bookingId: string): Promise<ICoreResponse> {
    return this.restClient.post(ADD_ONS_PATH.GET_ALL_ADD_ONS, {
      booking_id: bookingId,
    });
  }
  public saveAllAddOns(payload: ConfirmAddOns): Promise<ICoreResponse> {
    return this.restClient.post(ADD_ONS_PATH.POST_ALL_ADD_ONS, payload);
  }
  public getBooking(params: GetBookingPayloadType): Promise<ICoreResponse> {
    const PATH = formatStringToRoutePath(BOOKING_API_PATH.GET, {
      id: params.booking_id,
    });
    return this.restClient.get(PATH);
  }
  public confirmBooking(params: IConfirmBooking): Promise<ICoreResponse> {
    return this.restClient.post(BOOKING_API_PATH.POST, params);
  }
  public cancelBooking(
    params: ICancelBookingPayloadType,
  ): Promise<ICoreResponse> {
    return this.restClient.post(CANCEL_BOOKINGS_API_PATH.POST, params);
  }
  public downloadFlightTicket(
    params: GetDownloadBookingPayloadType,
  ): Promise<ICoreResponse> {
    const PATH = formatStringToRoutePath(DOWNLOAD_FLIGHT_TICKET.GET, {
      id: params.booking_id,
      render_fare: params.render_fare,
      is_edited: params.is_edited,
      per_adult_edited_fare: params.per_adult_edited_fare,
      per_child_edited_fare: params.per_child_edited_fare,
      per_infant_edited_fare: params.per_infant_edited_fare,
    });
    return this.restClient.get(
      PATH,
      {},
      {
        responseType: "blob",
      },
    );
  }

  public downloadNewFlightTicket(params: {
    booking_id: string;
    render_fare: string;
    is_edited: string;
    per_adult_edited_fare: string;
    per_child_edited_fare: string;
    per_infant_edited_fare: string;
  }): Promise<ICoreResponse> {
    const PATH = formatStringToRoutePath(
      NEW_FLIGHT_BOOKING_API_PATH.DOWNLOAD_TICKET,
      {
        id: params.booking_id,
        render_fare: params.render_fare,
        is_edited: params.is_edited,
        per_adult_edited_fare: params.per_adult_edited_fare,
        per_child_edited_fare: params.per_child_edited_fare,
        per_infant_edited_fare: params.per_infant_edited_fare,
      },
    );
    return this.restClient.get(
      PATH,
      {},
      {
        responseType: "blob",
      },
    );
  }
  public voidFlightBooking(
    params: ICommitVoidBookingPayloadType,
  ): Promise<ICoreResponse> {
    return this.restClient.post(VOID_TICKET.COMMIT_VOID, params);
  }
  public initiateVoidFlightBooking(
    params: IInitiateVoidBookingPayloadType,
  ): Promise<ICoreResponse> {
    return this.restClient.post(VOID_TICKET.INITIATE_VOID, params);
  }
  public getOrganizationPassengers(
    organizationId: string,
    passengerIds?: string[],
  ): Promise<ICoreResponse> {
    const params = new URLSearchParams();
    passengerIds?.forEach((id) => params.append("passenger_ids", id));

    return this.restClient.get(
      `${formatString(ORGANIZATION_PASSENGERSS.GET, organizationId)}?${params.toString()}`,
    );
  }
  public createPassengers(
    body: Traveler[],
    organizationId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(ORGANIZATION_PASSENGERSS.POST, organizationId),
      body,
    );
  }
  public editPassengersDetail(
    body: EditPassengerRequest,
    passengerId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.patch(
      formatString(BOOKING_API_PATH.EDIT_PASSENGERS, passengerId),
      { ...body },
    );
  }

  public async fetchTicketIssuanceDetails(
    _bookingId: string,
    payload: FetchTicketIssuanceDetailsPayload,
  ): Promise<TicketIssuanceDetailsResponse> {
    // Fetch PNR details. Use v3 PNR endpoint for Sabre, v2 for non-Sabre.
    const endpoint = payload.isSabreBooking
      ? FLIGHT_ISSUANCE_API_PATH.PNR_DETAILS_V3
      : FLIGHT_ISSUANCE_API_PATH.PNR_DETAILS;

    const response = await this.restClient.post(endpoint, {
      financial_profile_public_id: payload.financialProfileId,
      pnr: payload.pnr,
      comments: payload.comments || "",
      airline: payload.airlineProvider,
      ...(payload.isSabreBooking && {
        airline_code: payload.airlineCode,
      }),
      ...(payload.overrideSales && {
        approval_by: payload.approvalBy,
      }),
      ...(payload.overrideSales && {
        override_sales_validations: true,
      }),
      ...(payload.overrideOperations && {
        override_operations_validations: true,
      }),
      ...(payload.surcharges_xi && {
        surcharges_xi: payload.surcharges_xi,
      }),
      ...(payload.request_based_pricing && {
        request_based_pricing: payload.request_based_pricing,
      }),
    });

    const typedResponse = response as ICoreResponse;
    if (!typedResponse.success) {
      throw typedResponse;
    }

    const responseData =
      (typedResponse.data?.data as Record<string, unknown> | undefined) || {};

    return {
      otpSent: true,
      pricing: this.resolvePricing(responseData),
      reference: (responseData.reference as string) || payload.reference || "",
      fareUpdated: this.resolveFareUpdated(responseData),
      fares:
        (responseData.booking_price_details as
          Record<string, unknown>[] | undefined) || [],
    };
  }

  public async calculateFare(
    _bookingId: string,
    payload: {
      reference?: string;
      financial_profile_public_id: string;
      fare_public_ids: string[];
    },
  ): Promise<TicketIssuanceDetailsResponse> {
    const response = await this.restClient.post(
      FLIGHT_ISSUANCE_API_PATH.PQ_CALCULATE_FARE,
      {
        reference: payload.reference,
        financial_profile_public_id: payload.financial_profile_public_id,
        fare_public_ids: payload.fare_public_ids,
      },
    );

    const typedResponse = response as ICoreResponse;
    if (!typedResponse.success) {
      throw typedResponse;
    }

    const responseData =
      (typedResponse.data?.data as Record<string, unknown> | undefined) || {};

    return {
      otpSent: true,
      pricing: this.resolvePricing(responseData),
      reference: (responseData.reference as string) || payload.reference || "",
      fareUpdated: this.resolveFareUpdated(
        responseData,
        payload.fare_public_ids,
      ),
      fares:
        (responseData.booking_price_details as
          Record<string, unknown>[] | undefined) || [],
    };
  }

  public async applyTicketIssuancePromoCode(
    _bookingId: string,
    promoCode: string,
    payload: FetchTicketIssuanceDetailsPayload,
  ): Promise<ApplyTicketIssuancePromoResponse> {
    const endpoint = payload.isSabreBooking
      ? FLIGHTS_PROMO_API_PATH.APPLY_PROMO_V2
      : FLIGHTS_PROMO_API_PATH.APPLY_PROMO;

    const response = (await this.restClient.post(endpoint, {
      promo_code: promoCode,
      reference: payload.reference,
      is_sabre: payload.isSabreBooking,
      ...(payload.isSabreBooking && {
        pnr: payload.pnr,
        airline_code: payload.airlineCode,
        fare_public_ids: payload.fareQuoteIds,
      }),
    })) as ICoreResponse;

    if (!response.success) {
      throw response;
    }

    const data =
      (response.data?.data as Record<string, unknown> | undefined) || {};

    return {
      promoId: (data.promo_id as string | null) ?? null,
      promoCode: (data.promo_code as string) || promoCode,
      message: (data.message as string) || response.message || "",
      applicable: Boolean(data.applicable),
      originalPrice: this.mapPriceAmount(
        data.orignal_price ?? data.original_price,
      ),
      discountedPrice: this.mapPriceAmount(data.discounted_price),
      discountAmount: this.mapPriceAmount(data.discount_amount),
    };
  }

  public async removeTicketIssuancePromoCode(
    bookingId: string,
  ): Promise<{ success: boolean; bookingId: string }> {
    return {
      success: true,
      bookingId,
    };
  }

  public async issueTicket(
    bookingId: string,
    payload: IssueTicketPayload,
  ): Promise<{ success: boolean; bookingId: string; reference: string }> {
    const issueTicketPayload = {
      pnr: payload.pnr,
      reference: payload.reference,
      financial_profile_public_id: payload.financialProfileId,
      emails: payload.emails || [],
      otp: payload.otp,
      ...(payload.isSabreBooking && {
        fare_public_ids: payload.fareQuoteIds,
      }),
      ...(payload.user_total_fare != null &&
        Number.isFinite(payload.user_total_fare) && {
          user_total_fare: payload.user_total_fare,
        }),
      ...(payload.promoId && { promo_id: payload.promoId }),
      ...(payload.overrideSales && {
        approval_by: payload.approvalBy,
        override_sales_validations: true,
      }),
      ...(payload.overrideOperations && {
        override_operations_validations: true,
      }),
    };

    const endpoint =
      payload.isSabreBooking && payload.fareQuoteIds.length > 0
        ? FLIGHT_ISSUANCE_API_PATH.ISSUE_TICKET_V3
        : FLIGHT_ISSUANCE_API_PATH.ISSUE_TICKET;

    const response = (await this.restClient.post(
      endpoint,
      issueTicketPayload,
    )) as ICoreResponse;

    if (!response.success) {
      throw response;
    }

    const reference =
      (response.data?.data as { reference?: string } | undefined)?.reference ||
      payload.reference ||
      "";

    return {
      success: true,
      bookingId,
      reference,
    };
  }
}

export default FlightBookingService;
